import { parseUrl } from '../instance/urls.ts';
import type { SocialSite } from '../model/richText.ts';

/**
 * **The other sites an uploader links to** — the Twitch channel under a stream VOD, the Patreon in a
 * "support the show" block, the handle on X.
 *
 * The sibling of `inline.ts`: that file asks whether a link can be opened *here*, and this one asks
 * whether the app can say *where it goes*. Nothing is opened in-app, nothing is fetched, and the href
 * is untouched — all this decides is the mark in front of the link and the words on it, which is a
 * decision core makes so both front-ends make it the same way.
 *
 * The gain is a description that reads as a list of places rather than a wall of URLs:
 * `https://www.twitch.tv/brotundvideospiele` becomes `brotundvideospiele` behind Twitch's mark, which
 * is the part the reader was looking for. A URL is only a good label when nothing else names the
 * destination.
 *
 * **Hosts are matched exactly**, `inline.ts`'s rule and for its reason: `twitch.tv.example.net` ends
 * in one of these strings and is not Twitch, and a mark that can be borrowed by a lookalike is worse
 * than no mark at all.
 */

interface SocialSpec {
  readonly site: SocialSite;
  readonly hosts: readonly string[];
  /**
   * Path prefixes that name nothing: `bsky.app/profile/name` and `discord.com/invite/CODE` say the
   * same thing as the mark already does, so the label is the part after them.
   */
  readonly drop?: readonly string[];
}

const SITES: readonly SocialSpec[] = [
  { site: 'twitch', hosts: ['twitch.tv', 'www.twitch.tv', 'm.twitch.tv'] },
  { site: 'patreon', hosts: ['patreon.com', 'www.patreon.com'] },
  // One mark for both names. The site renamed itself and the old links did not, so a description
  // written in 2021 and one written today point at the same place.
  { site: 'x', hosts: ['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com', 'mobile.twitter.com'] },
  {
    site: 'facebook',
    hosts: ['facebook.com', 'www.facebook.com', 'm.facebook.com', 'web.facebook.com', 'fb.com', 'www.fb.com'],
  },
  { site: 'instagram', hosts: ['instagram.com', 'www.instagram.com'] },
  // `vm.` and `vt.` are the app's own share links. What they carry is a code rather than a name, so
  // the label says little — but the mark still says where the reader is about to go, which is the
  // part a bare `vm.tiktok.com/ZMabcdefg` cannot tell them.
  { site: 'tiktok', hosts: ['tiktok.com', 'www.tiktok.com', 'm.tiktok.com', 'vm.tiktok.com', 'vt.tiktok.com'] },
  { site: 'bluesky', hosts: ['bsky.app', 'www.bsky.app'], drop: ['profile/'] },
  {
    site: 'reddit',
    hosts: ['reddit.com', 'www.reddit.com', 'old.reddit.com', 'new.reddit.com', 'm.reddit.com', 'np.reddit.com'],
  },
  {
    site: 'discord',
    hosts: ['discord.gg', 'www.discord.gg', 'discord.com', 'www.discord.com', 'discordapp.com', 'www.discordapp.com'],
    drop: ['invite/'],
  },
];

const BY_HOST: ReadonlyMap<string, SocialSpec> = new Map(
  SITES.flatMap((spec) => spec.hosts.map((host) => [host, spec] as const)),
);

/**
 * What a label may be made of, and it is a whitelist rather than an escape.
 *
 * A shortened label hides the domain, which is the whole point and also the risk: `x.com/https:/…`
 * is a path anyone can register a route for, and drawn without its host it would read as a link to
 * somewhere else entirely. So a path becomes a label only when every segment is the plain stuff
 * account names are made of. Anything else keeps the URL as written, and still gets the mark.
 */
const PLAIN_PATH = /^[A-Za-z0-9._~@%+-]+(?:\/[A-Za-z0-9._~@%+-]+)*$/;

/**
 * How long a label may run before it is cut back, in characters. Long enough for
 * `r/subreddit/comments/abc123`, short enough not to swallow the line it sits in.
 */
const MAX_LABEL_LENGTH = 64;

/** What a cut label ends in, so the reader can see the address goes on. */
const ELISION = '/\u2026';

/** Path segments that name nobody. `facebook.com/profile.php?id=…` is the shape that asked for it. */
const UNINFORMATIVE: ReadonlySet<string> = new Set(['profile.php']);

export interface SocialLink {
  readonly site: SocialSite;
  /**
   * The path, without its site: `twitch.tv/somebody` is `somebody`. A path too long for a line is cut
   * back to whole segments and ends in an ellipsis ({@link cutToFit}). `null` when the URL carries
   * nothing worth showing (the site's front page) or nothing safe to show, and then the link keeps
   * the address the author wrote.
   */
  readonly label: string | null;
}

/** The site an outbound link goes to, or `null` for one this build has nothing to say about. */
export function readSocialLink(url: string): SocialLink | null {
  const parts = parseUrl(url);
  if (parts === null || (parts.scheme !== 'https' && parts.scheme !== 'http')) return null;
  const spec = BY_HOST.get(parts.host.split(':')[0] ?? '');
  if (spec === undefined) return null;
  return { site: spec.site, label: labelOf(parts.path, spec) };
}

function labelOf(rawPath: string, spec: SocialSpec): string | null {
  let path = rawPath.replace(/^\/+/, '').replace(/\/+$/, '');
  for (const prefix of spec.drop ?? []) {
    if (path.toLowerCase().startsWith(prefix)) path = path.slice(prefix.length);
  }
  if (path === '') return null;
  if (!PLAIN_PATH.test(path)) return null;
  if (UNINFORMATIVE.has(path.toLowerCase())) return null;
  return path.length <= MAX_LABEL_LENGTH ? path : cutToFit(path);
}

/**
 * A path too long to draw, cut back to the segments that fit:
 * `r/AskHistorians/comments/1abcdef/why_did_the_roman_empire_fall` becomes
 * `r/AskHistorians/comments/1abcdef/\u2026`.
 *
 * A reddit post is what asked for it. Its address ends in a copy of the post's own title, which is
 * both the longest part and the part the reader needs least, and before this the whole link fell back
 * to `https://www.reddit.com/r/…` — the site said twice and the community not at all.
 *
 * Whole segments only, and never a partial one: a label is read as a path, and half a name at the end
 * of it is a name that does not exist. The cut is a prefix of the real address rather than a summary
 * of it, so the mark, the label and the destination still agree; the ellipsis is what says there is
 * more. A first segment that is already too long keeps the URL as written, since there is nothing to
 * cut back to.
 */
function cutToFit(path: string): string | null {
  const segments = path.split('/');
  let label = segments[0] ?? '';
  if (label.length + ELISION.length > MAX_LABEL_LENGTH) return null;
  for (const segment of segments.slice(1)) {
    const next = `${label}/${segment}`;
    if (next.length + ELISION.length > MAX_LABEL_LENGTH) break;
    label = next;
  }
  return `${label}${ELISION}`;
}
